import type { ReactNode } from "react";
import {
  ArrowRight,
  BadgeCheck,
  ChevronDown,
  ChevronRight,
  FileText,
  Globe,
  Layers3,
  LucideIcon,
  Search,
  Server,
  Sparkles,
  Users,
} from "lucide-react";
import { BrandIconShapes, IconBrand } from "./icons";
import type { NavItem } from "@/app/content";

import { cn } from "./ui/utils";
import { Button } from "./ui/button";
import { MobileNavDrawer } from "./mobile-nav-drawer";

export const brandPalette = {
  green: "#A3E635",
  blue: "#3B82F6",
  purple: "#8B5CF6",
  teal: "#14B8A6",
  orange: "#F97316",
  yellow: "#FACC15",
  pink: "#EC4899",
  hotline: "#EC4899",
  red: "#EF4444",
  indigo: "#6366F1",
  ink: "#111111",
  paper: "#F7F2E8",
  paperSoft: "#FCFAF3",
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const semanticPalette = [
  brandPalette.yellow,
  brandPalette.purple,
  brandPalette.blue,
  brandPalette.pink,
  brandPalette.green,
  brandPalette.orange,
  brandPalette.indigo,
  brandPalette.red,
  brandPalette.teal,
];

type HeaderAction = {
  href: string;
  label: string;
  variant?: "default" | "secondary";
};

type BrandTone = "protocol" | "client" | "platform" | "selfhost";
export type SemanticTermTone =
  | "protocol"
  | "client"
  | "caller"
  | "responder"
  | "platform"
  | "selfhost"
  | "hotline";

type BrandCardProps = {
  tone: BrandTone;
  kicker: string;
  title: string;
  summary: string;
  bullets: string[];
};

type SectionProps = {
  id: string;
  eyebrow: string;
  title: string;
  description: string;
  children: ReactNode;
  className?: string;
};

type SectionHeaderProps = Pick<SectionProps, "eyebrow" | "title" | "description">;

type DocCardProps = {
  title: string;
  summary: string;
  accent: string;
  icon: "sparkles" | "layers" | "users" | "server" | "file" | "globe";
};

type CaseCardProps = {
  title: string;
  summary: string;
  accent: string;
  points: string[];
};

type NamingRow = {
  source: string;
  target: string;
  note: string;
};

type FlowStep = {
  step: string;
  title: string;
  description: string;
  tone: BrandTone | "caller" | "responder";
};

type MarketplaceCard = {
  title: string;
  summary: string;
  accent: string;
};

type Action = {
  label: string;
  href: string;
  variant?: "primary" | "secondary";
};

const semanticTermColors: Record<Exclude<SemanticTermTone, "hotline">, string> = {
  protocol: brandPalette.green,
  client: brandPalette.blue,
  caller: brandPalette.teal,
  responder: brandPalette.orange,
  platform: brandPalette.purple,
  selfhost: brandPalette.yellow,
};

export function getSemanticTermColor(tone: SemanticTermTone) {
  if (tone === "hotline") {
    return null;
  }
  return semanticTermColors[tone];
}

export function SemanticTerm({
  tone,
  children,
  className,
}: {
  tone: SemanticTermTone;
  children: ReactNode;
  className?: string;
}) {
  const color = getSemanticTermColor(tone);
  return (
    <span
      className={cn("font-black", className)}
      style={color ? { color } : undefined}
    >
      {children}
    </span>
  );
}

export function PageShell({ children }: { children: ReactNode }) {
  return (
    <div className="isolate relative min-h-screen overflow-x-hidden bg-[color:var(--surface)] text-[color:var(--ink)]">
      <BrandBackdrop />
      <div className="relative z-10">{children}</div>
    </div>
  );
}

export function BrandBackdrop() {
  return (
    <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
      <div className="absolute inset-0 grid grid-cols-3 grid-rows-3 opacity-40">
        {[
          brandPalette.yellow,
          brandPalette.purple,
          brandPalette.blue,
          brandPalette.pink,
          brandPalette.green,
          brandPalette.orange,
          brandPalette.indigo,
          brandPalette.red,
          brandPalette.teal,
        ].map((color, index) => (
          <div key={`${color}-${index}`} style={{ backgroundColor: color }} />
        ))}
      </div>

      <div className="absolute inset-0 opacity-20">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="brand-grid" x="0" y="0" width="200" height="200" patternUnits="userSpaceOnUse">
              {/* Transparent background so colored tiles show through */}
              <rect width="200" height="200" fill="none" />
              {/* Reuse the exact same IconBrand shapes — single source of truth */}
              <BrandIconShapes lineColor={brandPalette.ink} showText={true} />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#brand-grid)" />
        </svg>
      </div>

      <div className="absolute left-[10%] top-24 h-52 w-64 bg-black/11" />
      <div className="absolute bottom-32 right-[15%] h-60 w-72 bg-black/10 -rotate-6" />
      <div className="absolute top-[42%] right-[8%] h-56 w-56 rounded-full bg-black/12" />
      <div className="absolute bottom-[48%] left-[12%] h-48 w-48 rounded-full bg-black/10" />

      <div className="absolute left-20 top-20 h-64 w-64 rotate-12 bg-[#A3E635]/30" />
      <div className="absolute bottom-20 right-20 h-80 w-80 -rotate-12 bg-[#8B5CF6]/25" />
    </div>
  );
}

function NavLink({ item }: { item: NavItem }) {
  if (item.children) {
    return (
      <div className="group/dd relative">
        <button
          type="button"
          className="flex items-center gap-1 text-sm font-black uppercase text-black transition-colors hover:text-[#A3E635]"
        >
          {item.label}
          <ChevronDown className="h-3.5 w-3.5" strokeWidth={3} />
        </button>
        <div className="pointer-events-none absolute left-1/2 top-full z-50 w-72 -translate-x-1/2 pt-3 opacity-0 transition-all group-hover/dd:pointer-events-auto group-hover/dd:opacity-100 group-focus-within/dd:pointer-events-auto group-focus-within/dd:opacity-100">
          <div className="border-4 border-black bg-white shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
            {item.children.map((child) => {
              const isExternal = child.href.startsWith("http");
              return (
                <a
                  key={child.href}
                  href={child.href}
                  {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  className="block border-b-2 border-black/10 px-4 py-3 last:border-b-0 transition-colors hover:bg-[#A3E635]/20"
                >
                  <span className="text-sm font-black text-black">{child.label}</span>
                  {child.description ? (
                    <span className="mt-0.5 block text-xs font-bold text-zinc-500">{child.description}</span>
                  ) : null}
                </a>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <a href={item.href} className="text-sm font-black uppercase text-black transition-colors hover:text-[#A3E635]">
      {item.label}
    </a>
  );
}

export function TopNav({
  navItems,
  actionItems = [],
  homeHref = "/",
  mobileMenuLabel,
  mobileCloseLabel,
}: {
  navItems: NavItem[];
  actionItems?: HeaderAction[];
  homeHref?: string;
  mobileMenuLabel?: string;
  mobileCloseLabel?: string;
}) {
  const secondaryAction = actionItems.find((item) => item.variant === "secondary") || null;
  const primaryAction = actionItems.find((item) => item.variant !== "secondary") || null;
  return (
    <header className="sticky top-0 z-50 border-b-4 border-black bg-white/80 backdrop-blur-md">
      <div className="mx-auto flex h-24 max-w-7xl 2xl:max-w-[1440px] items-center justify-between gap-3 px-4 sm:gap-4 sm:px-6 lg:px-8">
        <div className="flex min-w-0 flex-1 items-center gap-3 sm:gap-4">
          <div className="xl:hidden">
            <MobileNavDrawer
              navItems={navItems}
              menuLabel={mobileMenuLabel ?? (homeHref.startsWith("/en") ? "Open menu" : "打开菜单")}
              closeLabel={mobileCloseLabel ?? (homeHref.startsWith("/en") ? "Close menu" : "关闭菜单")}
              navTitle={homeHref.startsWith("/en") ? "Menu" : "菜单"}
            />
          </div>
          <a href={homeHref} className="flex min-w-0 items-center gap-3 sm:gap-5">
            <IconBrand className="h-10 w-10 flex-shrink-0 border-4 border-black sm:h-12 sm:w-12" />
            <div className="flex min-w-0 flex-col items-start gap-1">
              <p className="text-lg font-black uppercase leading-none tracking-tight text-black sm:text-2xl">
                CALL ANYTHING
              </p>
              <p className="cursor-pointer bg-[#A3E635] px-1.5 py-0.5 text-[10px] font-black leading-none tracking-widest text-black transition-all duration-300 hover:-translate-y-0.5 hover:rotate-3 hover:bg-[#8B5CF6] hover:text-white hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] sm:text-xs">
                一呼百应
              </p>
            </div>
          </a>
        </div>

        <nav className="hidden items-center gap-8 xl:flex">
          {navItems.map((item) => (
            <NavLink key={item.href ?? item.label} item={item} />
          ))}
        </nav>

        <div className="flex flex-shrink-0 items-center gap-2 sm:gap-4">
          {secondaryAction ? (
            <Button asChild variant="secondary" className="hidden sm:inline-flex">
              <a href={secondaryAction.href}>{secondaryAction.label}</a>
            </Button>
          ) : null}
          {primaryAction ? (
            <Button
              asChild
              variant="default"
              size="sm"
              className="max-w-[10.5rem] whitespace-normal text-center leading-tight sm:max-w-none sm:px-6 sm:py-3 sm:text-sm"
            >
              <a href={primaryAction.href}>{primaryAction.label}</a>
            </Button>
          ) : null}
        </div>
      </div>
    </header>
  );
}

export function HeroSection({
  eyebrow,
  titleTop,
  titleBottom,
  lead,
  note,
  actions,
  facts,
}: {
  eyebrow: string;
  titleTop: string;
  titleBottom: string;
  lead: string;
  note: string;
  actions: Action[];
  facts: { label: string; value: string; note: string; color: string }[];
}) {
  return (
    <section id="top" className="px-4 py-10 sm:px-6 lg:px-8 lg:py-14">
      <div className="mx-auto grid max-w-7xl gap-8 xl:grid-cols-[1.1fr_0.9fr]">
        <div className="space-y-6">
          <div className="flex flex-wrap gap-3">
            <span className="border-4 border-black bg-white/75 backdrop-blur-sm px-4 py-2 text-xs font-black uppercase tracking-[0.3em] shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
              {eyebrow}
            </span>
            <span className="border-4 border-black bg-[#A3E635] px-4 py-2 text-xs font-black uppercase tracking-[0.3em] shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
              protocol anchored
            </span>
          </div>

          <div className="space-y-4">
            <div className="inline-block border-8 border-black bg-white/75 backdrop-blur-sm px-6 py-5 shadow-[12px_12px_0px_0px_rgba(0,0,0,1)]">
              <h1 className="text-6xl font-black uppercase leading-none tracking-[-0.08em] sm:text-7xl xl:text-8xl">{titleTop}</h1>
            </div>
            <div
              className="inline-block border-8 border-black px-6 py-5 shadow-[12px_12px_0px_0px_rgba(0,0,0,1)]"
              style={{ backgroundColor: brandPalette.green }}
            >
              <h1 className="text-6xl font-black uppercase leading-none tracking-[-0.08em] sm:text-7xl xl:text-8xl">{titleBottom}</h1>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
            <div className="border-4 border-black bg-white/75 backdrop-blur-sm p-6 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
              <p className="text-lg font-black uppercase leading-tight text-black sm:text-xl">{lead}</p>
              <p className="mt-4 text-sm font-bold leading-6 text-zinc-800">{note}</p>
            </div>
            <div className="border-4 border-black bg-black p-6 text-white shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
              <p className="text-xs font-black uppercase tracking-[0.3em] text-[#A3E635]">Brand grammar</p>
              <div className="mt-4 space-y-3 text-sm font-bold leading-6">
                <p>Protocol = green anchor.</p>
                <p>Client = blue base with role colors.</p>
                <p>Platform = purple infrastructure layer.</p>
                <p>Selfhost = yellow private deployment tier.</p>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            {actions.map((action) => (
              <a
                key={action.href}
                href={action.href}
                className={cn(
                  "border-4 border-black px-6 py-4 text-sm font-black uppercase shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] transition-transform hover:-translate-x-0.5 hover:-translate-y-0.5",
                  action.variant === "secondary" ? "bg-white text-black" : "bg-[#A3E635] text-black",
                )}
              >
                {action.label}
              </a>
            ))}
          </div>
        </div>

        <div className="grid gap-4">
          <div className="border-4 border-black bg-white/75 backdrop-blur-sm p-5 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
            <p className="text-xs font-black uppercase tracking-[0.3em] text-zinc-500">Semantic color map</p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
              {facts.map((fact) => (
                <div key={fact.label} className="border-4 border-black bg-white/75 backdrop-blur-sm">
                  <div className="h-10 border-b-4 border-black" style={{ backgroundColor: fact.color }} />
                  <div className="p-4">
                    <p className="text-xs font-black uppercase tracking-[0.26em] text-zinc-500">{fact.label}</p>
                    <p className="mt-2 text-2xl font-black uppercase leading-none">{fact.value}</p>
                    <p className="mt-2 text-sm font-bold text-zinc-700">{fact.note}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-1">
            <div className="border-4 border-black bg-[#A3E635] p-5 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
              <p className="text-xs font-black uppercase tracking-[0.3em] text-black">Current focus</p>
              <p className="mt-3 text-sm font-bold text-black">Brand官网 + 文档中心 + 案例展示。</p>
            </div>
            <div className="border-4 border-black bg-white/75 backdrop-blur-sm p-5 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
              <p className="text-xs font-black uppercase tracking-[0.3em] text-zinc-500">Future lane</p>
              <p className="mt-3 text-sm font-bold text-zinc-800">Marketplace can plug in later without breaking the scaffold.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function Section({ id, eyebrow, title, description, children, className }: SectionProps) {
  return (
    <section id={id} className={cn("scroll-mt-24 px-4 py-12 sm:px-6 lg:px-8 md:py-20", className)}>
      <div className="mx-auto max-w-7xl 2xl:max-w-[1440px]">
        <SectionHeader eyebrow={eyebrow} title={title} description={description} />
        <div className="mt-8">{children}</div>
      </div>
    </section>
  );
}

export function SectionHeader({ eyebrow, title, description }: SectionHeaderProps) {
  return (
    <div className="mb-10 flex flex-col items-start gap-4 sm:mb-14">
      <h2 className="inline-block border-4 border-black bg-white/75 backdrop-blur-sm px-6 py-4 text-4xl font-black uppercase text-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] sm:text-5xl lg:text-6xl">
        {eyebrow}
      </h2>
      <div className="mt-2 text-xl font-bold text-black sm:text-2xl drop-shadow-sm">
        {title}
      </div>
      {description && (
        <div className="border-l-8 border-[#A3E635] pl-4">
          <p className="max-w-4xl text-lg sm:text-xl font-black leading-relaxed text-zinc-900 drop-shadow-sm">
            {description}
          </p>
        </div>
      )}
    </div>
  );
}

export function BrandCard({ tone, kicker, title, summary, bullets }: BrandCardProps) {
  const topStrip =
    tone === "protocol"
      ? brandPalette.green
      : tone === "client"
        ? brandPalette.blue
        : tone === "platform"
          ? brandPalette.purple
          : brandPalette.yellow;

  return (
    <article className="border-4 border-black bg-white/75 backdrop-blur-sm shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
      {tone === "client" ? (
        <div className="flex h-14 border-b-4 border-black">
          <div className="flex-[1.2] bg-[#3B82F6]" />
          <div className="flex-1 border-l-4 border-black bg-[#14B8A6]" />
          <div className="flex-1 border-l-4 border-black bg-[#F97316]" />
        </div>
      ) : (
        <div className="h-14 border-b-4 border-black" style={{ backgroundColor: topStrip }} />
      )}

      <div className="p-5">
        <p className="text-xs font-black uppercase tracking-[0.3em] text-zinc-500">{kicker}</p>
        <h3 className="mt-3 text-3xl font-black uppercase leading-none">{title}</h3>
        <p className="mt-4 text-sm font-bold leading-6 text-zinc-800">{summary}</p>
        {tone === "client" ? (
          <div className="mt-5 grid grid-cols-2 border-4 border-black">
            <div className="border-r-4 border-black bg-[#14B8A6] p-4">
              <p className="text-xs font-black uppercase tracking-[0.26em] text-black">Caller</p>
              <p className="mt-2 text-sm font-bold text-black">Initiates the Call.</p>
            </div>
            <div className="bg-[#F97316] p-4">
              <p className="text-xs font-black uppercase tracking-[0.26em] text-black">Responder</p>
              <p className="mt-2 text-sm font-bold text-black">Answers the Call.</p>
            </div>
          </div>
        ) : (
          <ul className="mt-5 space-y-2 text-sm font-bold leading-6 text-zinc-800">
            {bullets.map((bullet) => (
              <li key={bullet} className="flex gap-3">
                <span className="mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center border-2 border-black bg-[#A3E635] text-[10px] font-black">
                  •
                </span>
                <span>{bullet}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </article>
  );
}

export function NamingTable({ rows }: { rows: NamingRow[] }) {
  return (
    <div className="overflow-hidden border-4 border-black bg-white/75 backdrop-blur-sm shadow-[10px_10px_0px_0px_rgba(0,0,0,1)]">
      <div className="grid grid-cols-3 border-b-4 border-black bg-black text-white">
        <div className="p-5 text-xs font-black uppercase tracking-[0.3em] text-[#A3E635]">Old</div>
        <div className="border-l-4 border-black p-5 text-xs font-black uppercase tracking-[0.3em] text-[#A3E635]">New</div>
        <div className="border-l-4 border-black p-5 text-xs font-black uppercase tracking-[0.3em] text-[#A3E635]">Meaning</div>
      </div>
      {rows.map((row, index) => (
        <div key={row.source} className={cn("grid grid-cols-3", index !== rows.length - 1 && "border-b-4 border-black")}>
          <div className="p-5 text-sm font-black uppercase tracking-[0.16em]">{row.source}</div>
          <div className="border-l-4 border-black bg-[#A3E635]/30 p-5 text-sm font-black uppercase tracking-[0.16em]">
            {row.target}
          </div>
          <div className="border-l-4 border-black p-5 text-sm font-bold leading-6 text-zinc-800">{row.note}</div>
        </div>
      ))}
    </div>
  );
}

export function FlowRail({ steps }: { steps: FlowStep[] }) {
  const toneToColor = (tone: FlowStep["tone"]) => {
    if (tone === "caller") return semanticTermColors.caller;
    if (tone === "responder") return semanticTermColors.responder;
    if (tone === "protocol") return semanticTermColors.protocol;
    if (tone === "platform") return semanticTermColors.platform;
    return semanticTermColors.client;
  };

  return (
    <div className="border-4 border-black bg-white/75 backdrop-blur-sm p-5 shadow-[10px_10px_0px_0px_rgba(0,0,0,1)]">
      <div className="flex flex-wrap gap-3">
        {steps.map((step, index) => (
          <div key={step.step} className="flex min-w-0 flex-1 items-stretch gap-3">
            <div className="min-w-0 flex-1 border-4 border-black bg-white/75 backdrop-blur-sm">
              <div className="h-10 border-b-4 border-black" style={{ backgroundColor: toneToColor(step.tone) }} />
              <div className="p-4">
                <p className="text-xs font-black uppercase tracking-[0.3em] text-zinc-500">{step.step}</p>
                <h3 className="mt-2 text-xl font-black uppercase leading-tight">{step.title}</h3>
                <p className="mt-3 text-sm font-bold leading-6 text-zinc-700">{step.description}</p>
              </div>
            </div>
            {index < steps.length - 1 ? (
              <div className="hidden items-center justify-center md:flex">
                <ChevronRight className="h-7 w-7 text-black" strokeWidth={3} />
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

export function DocCard({ title, summary, accent, icon }: DocCardProps) {
  const icons: Record<DocCardProps["icon"], LucideIcon> = {
    sparkles: Sparkles,
    layers: Layers3,
    users: Users,
    server: Server,
    file: FileText,
    globe: Globe,
  };

  const Icon = icons[icon];

  return (
    <article className="border-4 border-black bg-white/75 backdrop-blur-sm shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
      <div className="flex items-center justify-between gap-4 border-b-4 border-black px-4 py-3" style={{ backgroundColor: accent }}>
        <span className="text-sm font-black uppercase tracking-[0.25em] text-black">{title}</span>
        <Icon className="h-5 w-5 text-black" strokeWidth={3} />
      </div>
      <div className="p-4">
        <p className="text-sm font-bold leading-6 text-zinc-800">{summary}</p>
      </div>
    </article>
  );
}

export function CaseCard({ title, summary, accent, points }: CaseCardProps) {
  return (
    <article className="border-4 border-black bg-white/75 backdrop-blur-sm shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
      <div className="h-14 border-b-4 border-black" style={{ backgroundColor: accent }} />
      <div className="p-5">
        <p className="text-xs font-black uppercase tracking-[0.3em] text-zinc-500">Case</p>
        <h3 className="mt-3 text-2xl font-black uppercase leading-tight">{title}</h3>
        <p className="mt-4 text-sm font-bold leading-6 text-zinc-800">{summary}</p>
        <ul className="mt-5 space-y-2 text-sm font-bold leading-6 text-zinc-800">
          {points.map((point) => (
            <li key={point} className="flex gap-3">
              <BadgeCheck className="mt-0.5 h-5 w-5 shrink-0 text-black" strokeWidth={3} />
              <span>{point}</span>
            </li>
          ))}
        </ul>
      </div>
    </article>
  );
}

export function MarketplaceStub({ cards }: { cards: MarketplaceCard[] }) {
  return (
    <div className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
      <div className="border-4 border-black bg-white/75 backdrop-blur-sm p-6 shadow-[10px_10px_0px_0px_rgba(0,0,0,1)]">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center border-4 border-black bg-[#A3E635]">
            <Search className="h-6 w-6 text-black" strokeWidth={3} />
          </div>
          <div>
            <p className="text-xs font-black uppercase tracking-[0.3em] text-zinc-500">Optional entry</p>
            <h3 className="text-2xl font-black uppercase leading-none">Marketplace can plug in later</h3>
          </div>
        </div>
        <p className="mt-4 max-w-2xl text-sm font-bold leading-6 text-zinc-800">
          当前只需要留出入口心智，不需要把 marketplace 变成当前阶段的强依赖。接不上时，品牌官网和文档中心仍然要完整成立。
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <span className="border-2 border-black bg-[#A3E635] px-3 py-2 text-[11px] font-black uppercase">Search</span>
          <span className="border-2 border-black bg-[#3B82F6] px-3 py-2 text-[11px] font-black uppercase text-white">Categories</span>
          <span className="border-2 border-black bg-[#8B5CF6] px-3 py-2 text-[11px] font-black uppercase text-white">Providers</span>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-1">
        {cards.map((card) => (
          <article key={card.title} className="border-4 border-black bg-white/75 backdrop-blur-sm shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
            <div className="h-12 border-b-4 border-black" style={{ backgroundColor: card.accent }} />
            <div className="p-4">
              <p className="text-xs font-black uppercase tracking-[0.3em] text-zinc-500">{card.title}</p>
              <p className="mt-3 text-sm font-bold leading-6 text-zinc-800">{card.summary}</p>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

/**
 * Tail page CTA — OPC narrative pivot (S9, 2026-04-17).
 *
 * Layout intent (per spec §9):
 *   - Two cards in 7:3 ratio
 *     · Left  (7) RESPONDER · 超级个体  → 5 分钟开通你的第一根 Hotline   [primary]
 *     · Right (3) CALLER   · Agent 团队 → 5 分钟将 Hotline 接入 Agent     [secondary]
 *   - The asymmetry physically embodies "primary audience = supplier".
 *   - Footer strip stays as a Console / Marketplace / Docs entry point.
 */
export function TailPageCTA() {
  return (
    <section className="px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
      <div className="mx-auto max-w-7xl 2xl:max-w-[1440px]">
        <div className="text-center mb-10 sm:mb-14">
          <p className="text-xs font-black uppercase tracking-[0.3em] text-zinc-500">FROM HERE</p>
          <h2 className="mt-3 text-3xl font-black uppercase tracking-tight text-black sm:text-5xl lg:text-6xl">
            5 分钟跑通
          </h2>
          <p className="mt-4 text-base font-bold text-zinc-700 sm:text-lg">
            每一步都是仓库里真实存在的命令、真实的接口形状；不喜欢可以中途换。
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-[7fr_3fr]">
          {/* Primary — RESPONDER · 超级个体 */}
          <a
            href="/docs/quick-start-responder"
            className="group block border-[6px] border-black bg-[#3B82F6]/85 backdrop-blur-md p-6 sm:p-8 shadow-[12px_12px_0px_0px_rgba(0,0,0,1)] transition-all hover:-translate-x-1 hover:-translate-y-1 hover:shadow-[16px_16px_0px_0px_rgba(0,0,0,1)] hover:bg-[#3B82F6]/95"
          >
            <div className="flex items-center gap-3">
              <span className="border-2 border-black bg-black px-2 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-[#A3E635]">
                RESPONDER · 超级个体
              </span>
              <span className="text-xs font-black uppercase text-white/80">主推路径</span>
            </div>
            <h3 className="mt-5 text-3xl font-black uppercase text-white sm:text-4xl lg:text-5xl leading-tight">
              5 分钟开通你的
              <br />
              第一根 Hotline
            </h3>
            <ul className="mt-6 grid gap-2 sm:grid-cols-2 text-sm font-bold text-white">
              <li className="flex gap-2"><span>·</span><span>无需接入 Stripe</span></li>
              <li className="flex gap-2"><span>·</span><span>无需自建后台</span></li>
              <li className="flex gap-2"><span>·</span><span>核心代码不出本地</span></li>
              <li className="flex gap-2"><span>·</span><span>数字分身 7×24 承接调用</span></li>
            </ul>
            <div className="mt-8 inline-flex items-center gap-2 border-4 border-black bg-[#A3E635] px-5 py-3 text-base font-black uppercase text-black shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] transition-transform group-hover:translate-x-1">
              开通我的 Hotline
              <ArrowRight className="h-5 w-5" strokeWidth={3} />
            </div>
          </a>

          {/* Secondary — CALLER · Agent 团队 */}
          <a
            href="/docs/quick-start-caller"
            className="group block border-[6px] border-black bg-white/85 backdrop-blur-md p-6 sm:p-8 shadow-[10px_10px_0px_0px_rgba(0,0,0,1)] transition-all hover:-translate-x-1 hover:-translate-y-1 hover:shadow-[14px_14px_0px_0px_rgba(0,0,0,1)] hover:bg-white/95"
          >
            <div className="flex items-center gap-3">
              <span className="border-2 border-black bg-black px-2 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-[#A3E635]">
                CALLER · AGENT 团队
              </span>
            </div>
            <h3 className="mt-5 text-2xl font-black uppercase text-black sm:text-3xl leading-tight">
              5 分钟将 Hotline
              <br />
              接入 Agent
            </h3>
            <ul className="mt-5 space-y-2 text-xs font-bold text-zinc-800">
              <li>· npm install</li>
              <li>· 拉取 catalog</li>
              <li>· 跑通一次 example call</li>
            </ul>
            <div className="mt-6 inline-flex items-center gap-2 border-2 border-black bg-black px-4 py-2 text-sm font-black text-white transition-transform group-hover:translate-x-1">
              查看接入文档
              <ArrowRight className="h-4 w-4" strokeWidth={3} />
            </div>
          </a>
        </div>

        <div className="mt-10 border-4 border-black bg-white/85 backdrop-blur-sm p-5 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-sm font-bold text-zinc-800">
            想先看 Console 是什么样？6 个页面均可交互。
          </p>
          <div className="flex flex-wrap gap-3">
            <Button asChild>
              <a href="/console">查看 Console 原型</a>
            </Button>
            <Button asChild variant="secondary">
              <a href="/marketplace">浏览 Marketplace</a>
            </Button>
            <Button asChild variant="secondary">
              <a href="/docs">完整文档</a>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}

export function BackCoverFooter() {
  return (
    <footer className="bg-[#1a1a1a] text-white">
      <div className="mx-auto max-w-7xl 2xl:max-w-[1440px] px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-6 lg:gap-8">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-5">
              <IconBrand className="w-12 h-12 border-4 border-black flex-shrink-0" />
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-3xl font-black uppercase tracking-tight text-white leading-none">CALL ANYTHING</span>
                <span className="bg-[#A3E635] px-1.5 py-0.5 text-xs font-black tracking-widest text-black border-2 border-black leading-none transition-all duration-300 hover:bg-[#8B5CF6] hover:text-white hover:-translate-y-0.5 hover:rotate-3 hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] cursor-pointer">
                  一呼百应
                </span>
              </div>
            </div>
            <p className="mt-6 text-sm font-bold leading-6 text-zinc-400">
              Agent 调用外部能力的开放协议 · 像 HTTP 一样平直、像 GitHub 一样可发现
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 lg:col-span-4">
            <div>
              <h3 className="text-xs font-black uppercase tracking-[0.2em] text-white">产品</h3>
              <ul className="mt-6 space-y-4">
                {[
                  { label: "首页", href: "/" },
                  { label: "文档中心", href: "/docs" },
                  { label: "Marketplace", href: "/marketplace" },
                  { label: "信任与结算", href: "/trust-settlement" },
                  { label: "FAQ", href: "/faq" },
                  { label: "Glossary", href: "/glossary" },
                ].map((item) => (
                  <li key={item.label}>
                    <a href={item.href} className="text-sm font-bold text-zinc-400 hover:text-white transition-colors">{item.label}</a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-xs font-black uppercase tracking-[0.2em] text-white">开发者</h3>
              <ul className="mt-6 space-y-4">
                {[
                  { label: "Quick Start Responder", href: "/docs/quick-start-responder/" },
                  { label: "Quick Start Caller", href: "/docs/quick-start-caller/" },
                  { label: "Protocol Architecture", href: "/docs#protocol" },
                  { label: "Glossary", href: "/glossary" },
                ].map((item) => (
                  <li key={item.label}>
                    <a href={item.href} className="text-sm font-bold text-zinc-400 hover:text-white transition-colors">{item.label}</a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-xs font-black uppercase tracking-[0.2em] text-white">生态愿景 / Ecosystem</h3>
              <ul className="mt-6 space-y-4">
                {[
                  { label: "远期愿景", href: "/vision" },
                  { label: "超级个体模式", href: "/opc" },
                  { label: "Marketplace 路线图", href: "/marketplace-roadmap" },
                ].map((item) => (
                  <li key={item.label}>
                    <a href={item.href} className="text-sm font-bold text-zinc-400 hover:text-white transition-colors">{item.label}</a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-xs font-black uppercase tracking-[0.2em] text-white">GitHub</h3>
              <ul className="mt-6 space-y-4">
                {[
                  { label: "Use locally (CLI)", href: "https://github.com/hejiajiudeeyu/delegated-execution-client" },
                  { label: "Protocol", href: "https://github.com/nicekid1/delegated-execution-protocol" },
                  { label: "Self-host", href: "https://github.com/hejiajiudeeyu/delegated-execution-platform-selfhost" },
                  { label: "Workspace", href: "https://github.com/hejiajiudeeyu/delegated-execution-workspace" },
                ].map((item) => (
                  <li key={item.label}>
                    <a href={item.href} target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-zinc-400 hover:text-white transition-colors">{item.label}</a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-16 border-t border-zinc-800 pt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <p className="text-xs font-medium text-zinc-500">
            © 2025 CALL ANYTHING. All rights reserved.
          </p>
          <div className="flex gap-6">
            <a href="/docs" className="text-xs font-medium text-zinc-500 hover:text-white transition-colors">文档</a>
            <a href="/marketplace" className="text-xs font-medium text-zinc-500 hover:text-white transition-colors">市场</a>
            <a href="/trust-settlement" className="text-xs font-medium text-zinc-500 hover:text-white transition-colors">信任与结算</a>
            <a href="https://github.com/nicekid1/delegated-execution-protocol/blob/main/LICENSE" target="_blank" rel="noopener noreferrer" className="text-xs font-medium text-zinc-500 hover:text-white transition-colors">License</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

// --- NEW BRAND COMPONENTS ---

export function GlassCard({ children, bgClass = 'bg-white/40', hoverClass = 'hover:bg-white/60', className = '' }: { children: React.ReactNode, bgClass?: string, hoverClass?: string, className?: string }) {
  const isInteractive = className.includes('cursor-pointer');
  const activeState = isInteractive ? 'active:translate-x-[6px] active:translate-y-[6px] active:!shadow-none select-none' : '';
  return (
    <div className={`border-4 border-black ${bgClass} backdrop-blur-sm p-6 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] transition-all duration-150 ${hoverClass} ${activeState} ${className}`}>
      {children}
    </div>
  );
}

export function SolidBlock({ children, bgClass = 'bg-black text-white', hoverClass = '', className = '' }: { children: React.ReactNode, bgClass?: string, hoverClass?: string, className?: string }) {
  const isInteractive = className.includes('cursor-pointer');
  const activeState = isInteractive ? 'active:translate-x-[8px] active:translate-y-[8px] active:!shadow-none select-none' : '';
  return (
    <div className={`border-4 border-black ${bgClass} p-8 md:p-12 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] transition-all duration-150 ${hoverClass} ${activeState} ${className}`}>
      {children}
    </div>
  );
}

export function HighlightBanner({ 
  badgeText, 
  badgeColor = 'bg-black',
  shadowClass = 'shadow-[2px_2px_0px_0px_rgba(168,85,247,1)]',
  badgeIcon,
  children,
  className = ''
}: { 
  badgeText: string, 
  badgeColor?: string,
  shadowClass?: string,
  badgeIcon?: React.ReactNode,
  children: React.ReactNode,
  className?: string
}) {
  const isInteractive = className.includes('cursor-pointer');
  const activeState = isInteractive ? 'active:translate-x-[6px] active:translate-y-[6px] active:!shadow-none select-none' : '';
  return (
    <div className={`mt-2 border-4 border-black bg-white/75 backdrop-blur-sm p-5 lg:p-6 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] transition-all duration-150 hover:bg-white/95 flex flex-col md:flex-row items-start gap-4 md:items-center ${activeState} ${className}`}>
      <div className={`shrink-0 ${badgeColor} border-2 border-black px-3 py-1.5 ${shadowClass} flex items-center gap-2 transition-all duration-150`}>
        {badgeIcon}
        <span className="font-black text-white text-sm whitespace-nowrap">{badgeText}</span>
      </div>
      <div className="min-w-0 flex-1 text-sm font-bold leading-relaxed text-zinc-900">
        {children}
      </div>
    </div>
  );
}

export function BrutalistBadge({ 
  children, 
  bgClass = 'bg-white', 
  hoverBgClass = 'hover:bg-[#3B82F6]',
  hoverTextClass = 'hover:text-white',
  className = '' 
}: { 
  children: React.ReactNode, 
  bgClass?: string, 
  hoverBgClass?: string,
  hoverTextClass?: string,
  className?: string 
}) {
  return (
    <div className={`inline-block border-4 border-black ${bgClass} px-4 py-2 text-sm font-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-all duration-150 ${hoverBgClass} ${hoverTextClass} cursor-pointer select-none relative active:translate-x-[4px] active:translate-y-[4px] active:!shadow-none ${className}`}>
      {children}
    </div>
  );
}
